import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlignLeft,
  FileText,
  Gauge,
  Hash,
  ImagePlus,
  Layers,
  Scale,
  Shapes,
  Tags as TagsIcon,
  Type,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { DropZone } from '@/components/ui/drop-zone';
import SearchSelect from '@/components/ui/search-select';
import { useGetCategoriesQuery } from '@/redux/category/category.api.slice';
import { ICategoryType } from '@/redux/category/category.d';
import {
  useCreateTagMutation,
  useGetTagsQuery,
} from '@/redux/tag/tag.api.slice';
import { ITagType } from '@/redux/tag/tag.d';
import { thumbnailExtensions } from '@/redux/post/post.schema';
import {
  useUpdateProjectGroupMutation,
  useUpdateProjectGroupProjectsMutation,
} from '@/redux/projectGroup/projectGroup.api.slice';
import { IUpdateProjectGroupRequest } from '@/redux/projectGroup/projectGroup.d';
import { IPostAttachment, IPostTag } from '@/redux/post/post.d';
import { ICategory } from '@/redux/category/category';
import { useGetPreSignedUploadUrlMutation } from '@/redux/attachment/attachment.api.slice';
import {
  AttachmentParentType,
  AttachmentType,
  IAttachmentCategory,
} from '@/redux/attachment/attachment.d';
import hashFile from '@/lib/xxhash';
import Panel from './components/Panel';
import FieldLabel, { Required } from './components/FieldLabel';
import FieldError, { redlineControl } from './components/FieldError';
import DifficultyPicker from './components/DifficultyPicker';
import LicenseSelector from './components/LicenseSelector';
import TagInput from './components/TagInput';
import WizardFooter from './components/WizardFooter';
import ProjectPicker from './components/ProjectPicker';
import {
  STEP_HEADING,
  FIELD_LABEL,
  WIZARD_FOCUS,
} from './components/fileCardStyles';
import { fmtSize } from './utils/fileHelpers';
import {
  extractErrorMessage,
  LICENSE_TO_CC_CODE,
  uploadFileToPresignedUrl,
} from './utils/postSync';
import { restoreGroupCoverUrl, restoreGroupDetails } from './utils/restorePost';
import { missingForGroupEdit } from './utils/validation';
import useFieldErrors from './useFieldErrors';
import useFocusField from './useFocusField';
import LivePreview from './components/LivePreview';
import { TitleBar } from './UploadWizard';
import {
  addGroupTag,
  removeGroupTag,
  selectV2,
  setGroupCover,
  resetAll,
  setGroupDetails,
  setMode,
  setMultiStage,
} from './v2Upload.slice';

const HINT = 'text-xs text-muted-foreground mt-1.5';

const CC_CODE_TO_LICENCE: Record<string, string> = Object.fromEntries(
  Object.entries(LICENSE_TO_CC_CODE).map(([licence, code]) => [code, licence])
);

/** Only what the form touches — satisfied by both the full and the light group
 * response, so the caller doesn't have to fetch one shape or the other. */
export interface EditableGroup {
  _id: string;
  name: string;
  description: string;
  thumbnail: IPostAttachment | null;
  category: ICategory;
  difficulty?: string | null;
  licenceType?: string;
  tags: IPostTag[];
  projects: { _id: string; title: string }[];
}

interface Details {
  title: string;
  description: string;
  category: string;
  difficulty: string;
  tags: string[];
  license: string;
}

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

/** The rail's progress card. An edit is one step, so the list is one row —
 * the wizard's own six-step rail would be claiming work that does not exist. */
function EditProgress() {
  return (
    <div className="rounded-xl border border-border-strong bg-card p-4">
      <div className={cn(FIELD_LABEL, 'mb-1')}>Project progress</div>
      <p className="mb-2 text-[11px] text-muted-foreground">
        Editing an existing project
      </p>
      <div className="flex items-center gap-2.5 rounded-lg border border-primary/30 bg-accent px-3 py-2.5 text-left text-sm font-medium text-foreground">
        <span className="flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full bg-primary text-[9px] text-white">
          1
        </span>
        <span className="flex-1">Edit details</span>
        <span className="text-[10px] text-muted-foreground">Now</span>
      </div>
    </div>
  );
}

/**
 * Editing a group that has already been submitted: its own metadata and which
 * projects it bundles, and nothing else. Deliberately not the wizard — a group
 * edit never walks its children, never re-validates them, and never calls
 * submit, so the workpieces keep whatever approval they already hold.
 */
// fallow-ignore-next-line complexity
export default function GroupEditPage({ group }: { group: EditableGroup }) {
  const navigate = useNavigate();
  useFocusField();

  const dispatch = useDispatch();
  // The form is held in the wizard's own group slice, not local state, so the
  // rail's Live Preview reflects every keystroke the way it does mid-wizard.
  // Only the group's own fields are hydrated — never childIds, which is what
  // makes the wizard walk the workpieces.
  const { groupDetails, groupCover } = useSelector(selectV2);
  const details = groupDetails;
  const cover = groupCover;
  const restoredCover = restoreGroupCoverUrl(group);
  const [saving, setSaving] = useState(false);

  // One effect owns both directions: hydrate the slice from the group, and hand
  // it back clean on the way out so the next upload does not resume into this
  // group. Split in two, StrictMode's remount ran the reset between them and
  // left the form empty.
  const groupRef = useRef(group);
  groupRef.current = group;
  useEffect(() => {
    const g = groupRef.current;
    const url = restoreGroupCoverUrl(g);
    dispatch(setMode('multi'));
    dispatch(setMultiStage('review'));
    dispatch(setGroupDetails(restoreGroupDetails(g)));
    dispatch(setGroupCover(url ? { preview: url } : null));
    return () => {
      dispatch(resetAll());
    };
  }, [group._id, dispatch]);

  const originalIds = useMemo(
    () => (group.projects ?? []).map((p) => p._id),
    [group]
  );
  const [selectedIds, setSelectedIds] = useState<string[]>(originalIds);

  const { data: categories } = useGetCategoriesQuery({
    type: ICategoryType.POST,
  });
  const { data: tags } = useGetTagsQuery({ type: ITagType.POST });
  const [createTag] = useCreateTagMutation();
  const [updateProjectGroup] = useUpdateProjectGroupMutation();
  const [updateProjectGroupProjects] = useUpdateProjectGroupProjectsMutation();
  const [getPreSignedUploadUrl] = useGetPreSignedUploadUrlMutation();

  const missing = missingForGroupEdit(details, selectedIds);
  const { bad, props, guard } = useFieldErrors(missing);

  const update = (patch: Partial<Details>) => dispatch(setGroupDetails(patch));

  const toggleProject = (id: string) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const dropCover = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) return;
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!thumbnailExtensions.includes(ext)) {
      toast.error('Invalid file type', {
        description: `A cover has to be ${thumbnailExtensions.join(', ')}`,
      });
      input.value = '';
      return;
    }
    dispatch(setGroupCover({ file, preview: URL.createObjectURL(file) }));
  };

  /** The form holds category and tag NAMES; the group API wants ids. */
  const resolveCategoryId = (): string | undefined =>
    (categories ?? []).find(
      (c) =>
        c.name.trim().toLowerCase() === details.category.trim().toLowerCase()
    )?._id;

  // fallow-ignore-next-line complexity
  const resolveTagIds = async (): Promise<string[]> =>
    Promise.all(
      // fallow-ignore-next-line complexity
      details.tags.map(async (name) => {
        const existing = tags?.find(
          (t) => t.name.trim().toLowerCase() === name.trim().toLowerCase()
        );
        if (existing?._id) return existing._id as string;
        const created = await createTag({ name, type: ITagType.POST }).unwrap();
        if (!created?._id) throw new Error(`Could not create tag "${name}"`);
        return created._id as string;
      })
    );

  // fallow-ignore-next-line complexity
  const buildPatch = (tagIds: string[]): IUpdateProjectGroupRequest => {
    const categoryId = resolveCategoryId();
    const licenceType = CC_CODE_TO_LICENCE[details.license];
    return {
      name: details.title,
      description: details.description,
      tags: tagIds,
      // Clearing an existing cover has to reach the server; a new one is
      // attached by uploadCover instead.
      ...(restoredCover && !cover ? { thumbnail: null } : {}),
      ...(categoryId ? { category: categoryId } : {}),
      ...(details.difficulty ? { difficulty: details.difficulty } : {}),
      ...(licenceType ? { licenceType } : {}),
    };
  };

  // fallow-ignore-next-line complexity
  const uploadCover = async () => {
    const file = cover?.file;
    if (!file) return;
    const fileHash = await hashFile(file);
    const [signed] = await getPreSignedUploadUrl({
      attachments: [
        {
          parentId: group._id,
          parentType: AttachmentParentType.PROJECT_GROUP,
          category: IAttachmentCategory.projectGroupThumbnail,
          fileName: file.name,
          fileHash: fileHash.toString(),
          fileSize: file.size,
          resourceType: AttachmentType.image,
        },
      ],
    }).unwrap();
    if (signed?.signedUrl)
      await uploadFileToPresignedUrl(file, signed.signedUrl);
    if (signed?.attachmentId) {
      await updateProjectGroup({
        id: group._id,
        data: { thumbnail: signed.attachmentId.toString() },
      }).unwrap();
    }
  };

  // A no-op PUT still re-stamps the group, so only send one when something moved.
  // Compared with the same mapper the hydration used, so the two cannot drift.
  // fallow-ignore-next-line complexity
  const metadataChanged = () => {
    const original = restoreGroupDetails(group);
    const moved = (Object.keys(original) as (keyof Details)[]).some((k) =>
      k === 'tags'
        ? details.tags.join('|') !== (original.tags ?? []).join('|')
        : details[k] !== original[k]
    );
    return moved || Boolean(cover?.file) || (!cover && Boolean(restoredCover));
  };

  // fallow-ignore-next-line complexity
  const handleSave = async () => {
    if (!guard()) return;
    setSaving(true);
    try {
      if (metadataChanged()) {
        const tagIds = await resolveTagIds();
        await updateProjectGroup({
          id: group._id,
          data: buildPatch(tagIds),
        }).unwrap();
      }
      await uploadCover();
      // Membership is its own endpoint, and only worth a round trip when it
      // actually moved — it re-stamps projectGroupId on both sides of the diff.
      if (!sameSet(selectedIds, originalIds)) {
        await updateProjectGroupProjects({
          id: group._id,
          data: { projects: selectedIds },
        }).unwrap();
      }
      toast.success('Group updated');
      navigate(`/project-groups/${group._id}`);
    } catch (err) {
      toast.error('Could not save', {
        description: extractErrorMessage(
          err,
          'Something went wrong while saving. Please try again.'
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-secondary h-full flex flex-col font-secondary">
      <TitleBar
        onClose={() => navigate(`/project-groups/${group._id}`)}
        onHome={() => navigate('/')}
      />

      <div className="flex flex-1 min-h-0">
        <div
          className={cn(
            'flex-1 flex flex-col ml-1.5 sm:ml-3 mt-3 rounded-t-2xl border border-b-0 shadow-md p-4 sm:p-7 pb-0 sm:pb-0 min-w-0 overflow-y-auto custom-scrollbar bg-card',
            WIZARD_FOCUS
          )}
        >
          <div className="flex flex-col min-h-full">
            <h2 className={STEP_HEADING}>Edit project</h2>
            <p className="text-sm text-muted-foreground mb-6 max-w-[640px]">
              Change the name, details and which projects this set bundles. The
              workpieces themselves are left exactly as they are.
            </p>

            <div className="max-w-[1400px] space-y-5">
              <Panel
                icon={FileText}
                title="Project Basics"
                help="The name and blurb a maker reads first. Say what the whole project makes — 'Walnut desk set' beats 'my parts'."
              >
                <div {...props('title')}>
                  <FieldLabel icon={Type}>
                    Project Name
                    <Required />
                  </FieldLabel>
                  <Input
                    value={details.title}
                    onChange={(e) => update({ title: e.target.value })}
                    placeholder="e.g. Walnut desk set"
                    className={redlineControl(bad('title'))}
                  />
                  <FieldError
                    missing={missing}
                    field="title"
                    show={bad('title')}
                  />
                </div>

                <div {...props('description')}>
                  <FieldLabel icon={AlignLeft}>
                    Short Description
                    <Required />
                  </FieldLabel>
                  <Textarea
                    value={details.description}
                    onChange={(e) => update({ description: e.target.value })}
                    placeholder="What the set makes, and how the pieces fit together…"
                    rows={3}
                    className={redlineControl(bad('description'))}
                  />
                  <FieldError
                    missing={missing}
                    field="description"
                    show={bad('description')}
                  />
                </div>
              </Panel>

              <Panel
                icon={TagsIcon}
                title="Project Info"
                help="How people find this project: the category decides where it turns up when they browse, and tags are what search matches on."
                className="overflow-visible"
              >
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div {...props('category')}>
                    <FieldLabel icon={Shapes}>
                      Category
                      <Required />
                    </FieldLabel>
                    <SearchSelect
                      aria-label="Category"
                      value={details.category}
                      options={(categories ?? []).map((c) => c.name)}
                      onChange={(v) => update({ category: v })}
                      placeholder="Select a category"
                      invalid={bad('category')}
                    />
                    <FieldError
                      missing={missing}
                      field="category"
                      show={bad('category')}
                    />
                  </div>

                  <div {...props('difficulty')}>
                    <FieldLabel icon={Gauge}>
                      Difficulty
                      <Required />
                    </FieldLabel>
                    <DifficultyPicker
                      label="Project Difficulty"
                      value={details.difficulty}
                      onChange={(v) => update({ difficulty: v })}
                      invalid={bad('difficulty')}
                    />
                    <FieldError
                      missing={missing}
                      field="difficulty"
                      show={bad('difficulty')}
                    />
                  </div>
                </div>

                <div {...props('tags')}>
                  <FieldLabel icon={Hash}>Tags</FieldLabel>
                  <TagInput
                    tags={details.tags}
                    onAdd={(t) => dispatch(addGroupTag(t))}
                    onRemove={(i) => dispatch(removeGroupTag(i))}
                    invalid={bad('tags')}
                  />
                  <FieldError
                    missing={missing}
                    field="tags"
                    show={bad('tags')}
                  />
                </div>
              </Panel>

              <Panel
                icon={ImagePlus}
                title="Cover Image"
                help="The one image the project card leads with. Without it the card falls back to the first workpiece's photo."
              >
                <div data-focus="cover">
                  {cover ? (
                    <div className="flex items-center gap-3 rounded-lg border p-2">
                      <img
                        src={cover.preview}
                        alt="Cover"
                        className="h-14 w-20 shrink-0 rounded-md border bg-muted object-cover"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] font-medium">
                          {cover.file?.name ?? 'Current cover'}
                        </p>
                        <p className={cn(HINT, 'mt-0.5')}>
                          {cover.file
                            ? fmtSize(cover.file.size)
                            : 'Drop a new image to replace it.'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => dispatch(setGroupCover(null))}
                        aria-label="Remove cover image"
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <DropZone onDrop={dropCover} className="border-input py-10">
                      <span className="pointer-events-none flex flex-col items-center gap-2">
                        <ImagePlus className="h-7 w-7 text-muted-foreground/50" />
                        <span className="text-[13px] font-medium">
                          Drop a cover image, or click to choose
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {thumbnailExtensions.join(', ')} — optional
                        </span>
                      </span>
                    </DropZone>
                  )}
                </div>
              </Panel>

              <Panel
                icon={Scale}
                title="License"
                help="How other makers may reuse this project."
              >
                <LicenseSelector
                  selected={details.license}
                  onSelect={(code) => update({ license: code })}
                />
              </Panel>

              <Panel
                icon={Layers}
                title="Projects in this set"
                help="Search your own projects to add or remove one. A project already bundled into another group can't be claimed twice."
                className="overflow-visible"
                actions={
                  <Badge variant="secondary">
                    {selectedIds.length} selected
                  </Badge>
                }
              >
                <div {...props('projects')}>
                  <ProjectPicker
                    selectedIds={selectedIds}
                    onToggle={toggleProject}
                    currentGroupId={group._id}
                    initialSelected={(group.projects ?? []).map((p) => ({
                      _id: p._id,
                      title: p.title,
                    }))}
                  />
                  <FieldError
                    missing={missing}
                    field="projects"
                    show={bad('projects')}
                  />
                </div>
              </Panel>
            </div>

            <WizardFooter
              onBack={() => navigate(`/project-groups/${group._id}`)}
              onContinue={handleSave}
              busy={saving}
              busyLabel="Saving…"
              backDisabled={saving}
              continueDisabled={saving}
              continueLabel="Save changes"
            />
          </div>
        </div>

        {/* Right rail — the wizard's own, with the step list collapsed to one. */}
        <div className="relative z-10 w-[320px] flex-none p-5 hidden md:block overflow-y-auto custom-scrollbar">
          <div data-focus="preview">
            <LivePreview />
          </div>
          <div className="mt-5" data-focus="progress">
            <EditProgress />
          </div>
        </div>
      </div>
    </div>
  );
}
